import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import axios from 'axios';
import { API_URL } from '../config';

const benefitItems = [
    {
        title: 'Tailored walkthrough',
        description: 'See the exact QR and customer engagement flow that fits your business.',
        icon: <InsightsRoundedIcon fontSize="small" />,
    },
    {
        title: 'Team-ready setup',
        description: 'We cover onboarding, dashboard workflows, and rollout best practices.',
        icon: <Groups2RoundedIcon fontSize="small" />,
    },
    {
        title: 'Fast scheduling',
        description: 'Pick a preferred date and we will follow up quickly to confirm the session.',
        icon: <EventAvailableRoundedIcon fontSize="small" />,
    },
];

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255,255,255,0.96)',
    },
};

export default function DemoModal({ buttonSx, fullWidthOverride }) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState('idle');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        demoType: '',
        preferredDate: '',
        description: ''
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileInfo = useMediaQuery(theme.breakpoints.down('sm'));
    const isFullWidth = typeof fullWidthOverride === 'boolean' ? fullWidthOverride : isSmall;

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setStatus('idle');
        setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            demoType: '',
            preferredDate: '',
            description: ''
        });
    };

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        try {
            const response = await axios.post(API_URL + 'booking_handler.php', formData);
            if (response.data.success) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        }
    };

    return (
        <>
            <Button
                variant="contained"
                onClick={handleOpen}
                fullWidth={isFullWidth}
                sx={{
                    borderRadius: 999,
                    px: 2.5,
                    py: 1.1,
                    boxShadow: `0 14px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                    ...buttonSx,
                }}
            >
                Book a Demo
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="md"
                scroll="paper"
                PaperProps={{
                    sx: {
                        maxHeight: '100dvh',
                        overflowY: { xs: 'auto', md: 'hidden' },
                        borderRadius: { xs: 0, sm: 4 },
                        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f5f7ff', 0.96)} 100%)`,
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'grid',
                        minHeight: { md: 'min(720px, 88dvh)' },
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 0.9fr) minmax(0, 1.1fr)' },
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            px: { xs: 2, sm: 3.5 },
                            py: { xs: 2.25, sm: 4 },
                            color: theme.palette.common.white,
                            background: 'linear-gradient(155deg, #4f46e5 0%, #7c3aed 52%, #06b6d4 100%)',
                        }}
                    >
                        <IconButton
                            aria-label="Close demo dialog"
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                top: 14,
                                right: 14,
                                color: theme.palette.common.white,
                                backgroundColor: alpha('#ffffff', 0.14),
                                '&:hover': {
                                    backgroundColor: alpha('#ffffff', 0.24),
                                },
                            }}
                        >
                            <CloseRoundedIcon />
                        </IconButton>

                        <Stack
                            spacing={isMobileInfo ? 2 : 2.5}
                            sx={{
                                maxWidth: 360,
                                pr: { xs: 4, md: 2 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: isMobileInfo ? 46 : 56,
                                    height: isMobileInfo ? 46 : 56,
                                    borderRadius: isMobileInfo ? 2 : 2.5,
                                    display: 'grid',
                                    placeItems: 'center',
                                    backgroundColor: alpha('#ffffff', 0.16),
                                    boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.18)}`,
                                }}
                            >
                                <CalendarMonthRoundedIcon />
                            </Box>

                            <Stack spacing={1}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        letterSpacing: isMobileInfo ? '0.14em' : '0.18em',
                                        opacity: 0.8,
                                        fontSize: isMobileInfo ? '0.65rem' : undefined,
                                    }}
                                >
                                    Personalized walkthrough
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: { xs: '1.45rem', sm: '2.2rem' },
                                        lineHeight: { xs: 1.12, sm: 1.08 },
                                        maxWidth: { xs: 260, sm: 'none' },
                                    }}
                                >
                                    Book a demo that feels like your brand.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: alpha('#ffffff', 0.86),
                                        maxWidth: 320,
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                    }}
                                >
                                    {isMobileInfo
                                        ? 'Tell us your goals and we will tailor the session to your workflow.'
                                        : 'Tell us how you want to use the platform and we will tailor the session to your workflow.'}
                                </Typography>
                            </Stack>

                            <Stack spacing={isMobileInfo ? 1 : 1.5}>
                                {benefitItems.map((item) => (
                                    <Box
                                        key={item.title}
                                        sx={{
                                            display: 'flex',
                                            gap: isMobileInfo ? 1 : 1.5,
                                            alignItems: 'flex-start',
                                            p: isMobileInfo ? 1.1 : 1.5,
                                            borderRadius: isMobileInfo ? 2 : 2.5,
                                            backgroundColor: alpha('#ffffff', 0.12),
                                            border: `1px solid ${alpha('#ffffff', 0.14)}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flexShrink: 0,
                                                width: isMobileInfo ? 30 : 34,
                                                height: isMobileInfo ? 30 : 34,
                                                borderRadius: isMobileInfo ? 1.5 : 1.75,
                                                display: 'grid',
                                                placeItems: 'center',
                                                backgroundColor: alpha('#ffffff', 0.14),
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: isMobileInfo ? '1rem' : '1.25rem',
                                                },
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: isMobileInfo ? '0.92rem' : '0.98rem', lineHeight: 1.25 }}>
                                                {item.title}
                                            </Typography>
                                            {!isMobileInfo && (
                                                <Typography sx={{ mt: 0.4, color: alpha('#ffffff', 0.8), fontSize: '0.92rem' }}>
                                                    {item.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Stack>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        <DialogContent
                            sx={{
                                p: { xs: 2.5, sm: 3.5 },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5,
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
                                    Schedule a Demo
                                </Typography>
                                <Typography sx={{ color: theme.palette.text.secondary }}>
                                    Share a few details and we&apos;ll arrange the best time and format for your session.
                                </Typography>
                            </Stack>

                            {status === 'error' && (
                                <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                                    Error submitting form. Please try again.
                                </Alert>
                            )}

                            {status === 'success' ? (
                                <Box
                                    sx={{
                                        py: { xs: 2, sm: 5 },
                                        px: { xs: 0.5, sm: 1 },
                                        textAlign: { xs: 'left', sm: 'center' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mb: 2,
                                            width: 68,
                                            height: 68,
                                            mx: { xs: 0, sm: 'auto' },
                                            borderRadius: '50%',
                                            display: 'grid',
                                            placeItems: 'center',
                                            color: theme.palette.common.white,
                                            background: 'linear-gradient(135deg, #5b5df0 0%, #06b6d4 100%)',
                                            boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.24)}`,
                                        }}
                                    >
                                        <EventAvailableRoundedIcon sx={{ fontSize: 34 }} />
                                    </Box>
                                    <Typography variant="h5" gutterBottom>
                                        You&apos;re booked in our queue!
                                    </Typography>
                                    <Typography sx={{ maxWidth: 420, mx: { xs: 0, sm: 'auto' }, color: theme.palette.text.secondary }}>
                                        Thank you! We&apos;ll contact you shortly to confirm the details of your demo.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: 2,
                                    }}
                                >
                                    <TextField
                                        required
                                        label="Full Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={fieldStyles}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonRoundedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        required
                                        label="Email"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={fieldStyles}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <MailOutlineRoundedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        required
                                        label="Phone Number"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={fieldStyles}
                                        inputProps={{
                                            pattern: '\\+?[0-9\\s-()]{7,}',
                                            title: 'Please enter a valid phone number'
                                        }}
                                        placeholder="+1 234 567 8900"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIphoneRoundedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={fieldStyles}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BusinessRoundedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <FormControl fullWidth sx={{ ...fieldStyles, gridColumn: { xs: 'span 1', sm: 'span 1' } }}>
                                        <InputLabel>Preferred Demo Type</InputLabel>
                                        <Select
                                            required
                                            name="demoType"
                                            value={formData.demoType}
                                            label="Preferred Demo Type"
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="Physical">Physical</MenuItem>
                                            <MenuItem value="Online">Online</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        required
                                        label="Preferred Date"
                                        type="date"
                                        name="preferredDate"
                                        value={formData.preferredDate}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        sx={fieldStyles}
                                        inputProps={{
                                            min: new Date().toISOString().split('T')[0],
                                        }}
                                    />

                                    <TextField
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        multiline
                                        rows={4}
                                        fullWidth
                                        sx={{
                                            ...fieldStyles,
                                            gridColumn: { xs: 'span 1', sm: '1 / -1' },
                                        }}
                                        placeholder="Tell us about your use case, team size, or goals for the demo."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.4 }}>
                                                    <NotesRoundedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            )}
                        </DialogContent>

                        <Divider />

                        <DialogActions
                            sx={{
                                px: { xs: 2.5, sm: 3.5 },
                                py: { xs: 2, sm: 2.5 },
                                justifyContent: 'space-between',
                                gap: 1.5,
                                flexDirection: { xs: 'column-reverse', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                            }}
                        >
                            {status !== 'success' ? (
                                <>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1 }}>
                                        We usually reply within one business day.
                                    </Typography>
                                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                        <Button onClick={handleClose} variant="outlined" fullWidth={isSmall}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={status === 'submitting'}
                                            fullWidth={isFullWidth}
                                        >
                                            {status === 'submitting' ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Book Demo'
                                            )}
                                        </Button>
                                    </Stack>
                                </>
                            ) : (
                                <Button onClick={handleClose} variant="contained" fullWidth={isSmall}>
                                    Close
                                </Button>
                            )}
                        </DialogActions>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
}
